import React from 'react';
import {
  AssistantRuntimeProvider,
  ComposerPrimitive,
  ThreadPrimitive,
  useLocalRuntime,
  useThread,
} from '@assistant-ui/react';
import type { ChatModelAdapter, ThreadMessage } from '@assistant-ui/react';
import Layout from '../components/Layout';
import { sendCrmCopilotRequest } from '../api/CrmCopilot';
import type {
  CrmCard,
  CrmClientAction,
  CrmCopilotResponse,
  CrmProvenance,
  PendingCrmAction,
} from '../types/CrmCopilot';
import '../css/SalesCopilotPage.css';

type SalesCopilotMessageMeta = Pick<
  CrmCopilotResponse,
  'requestId' | 'provenance' | 'cards' | 'pendingAction' | 'disambiguation' | 'recentContext' | 'errors'
>;

const META_KEY = 'crmCopilot';

const extractTextParts = (message: ThreadMessage) => {
  let text = '';

  for (const part of message.content) {
    if (part.type === 'text') {
      text += `${text ? '\n' : ''}${part.text}`;
    }
  }

  return text.trim();
};

const getLatestUserText = (messages: readonly ThreadMessage[]) => {
  const userMessages = [...messages].reverse().find((message) => message.role === 'user');
  if (!userMessages) return '';
  return extractTextParts(userMessages);
};

const getMessageText = (message: ThreadMessage) => extractTextParts(message);

const getMessageMeta = (message: ThreadMessage): SalesCopilotMessageMeta | undefined => {
  const meta = message.metadata?.custom?.[META_KEY];
  return meta && typeof meta === 'object' ? (meta as SalesCopilotMessageMeta) : undefined;
};

const toAssistantResult = (response: CrmCopilotResponse) => ({
  content: [{ type: 'text' as const, text: response.reply }],
  metadata: {
    custom: {
      [META_KEY]: {
        requestId: response.requestId,
        provenance: response.provenance,
        cards: response.cards,
        pendingAction: response.pendingAction,
        disambiguation: response.disambiguation,
        recentContext: response.recentContext,
        errors: response.errors,
      } satisfies SalesCopilotMessageMeta,
    },
  },
});

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return 'Not provided';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(formatValue).join(', ');
  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .map(([key, item]) => `${key}: ${formatValue(item)}`)
      .join(' | ');
  }
  return String(value);
};

const toLabel = (value: string) =>
  value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());

const ProvenanceBadge = ({ provenance }: { provenance: CrmProvenance }) => (
  <span className={`sales-copilot-badge provenance-${provenance.toLowerCase().replace(/\s+/g, '-')}`}>
    {provenance}
  </span>
);

const CardRenderer = ({
  card,
  onConfirmAction,
  onCancelAction,
  onSelectDisambiguation,
  busyActionId,
}: {
  card: CrmCard;
  onConfirmAction: (actionId: string) => void;
  onCancelAction: (actionId?: string) => void;
  onSelectDisambiguation: (groupId: string, selectionId: string, label: string) => void;
  busyActionId: string | null;
}) => {
  if (card.type === 'customer_summary' || card.type === 'query_summary' || card.type === 'followup_list') {
    return (
      <section className="sales-copilot-card">
        <div className="sales-copilot-card-title">{card.title}</div>
        <div className="sales-copilot-card-list">
          {card.items.map((item, index) => (
            <div key={`${card.type}-${index}`} className="sales-copilot-card-list-item">
              {Object.entries(item).map(([key, value]) => (
                <div key={key} className="sales-copilot-kv-row">
                  <span className="sales-copilot-kv-key">{toLabel(key)}</span>
                  <span className="sales-copilot-kv-value">{formatValue(value)}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (card.type === 'dashboard_summary') {
    return (
      <section className="sales-copilot-card">
        <div className="sales-copilot-card-title">{card.title}</div>
        <div className="sales-copilot-metrics-grid">
          {Object.entries(card.metrics).map(([key, value]) => (
            <div key={key} className="sales-copilot-metric">
              <span className="sales-copilot-metric-label">{toLabel(key)}</span>
              <strong className="sales-copilot-metric-value">{formatValue(value)}</strong>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (card.type === 'disambiguation') {
    return (
      <section className="sales-copilot-card">
        <div className="sales-copilot-card-title">{card.title}</div>
        <div className="sales-copilot-card-subtitle">Choose one {card.entity} to continue.</div>
        <div className="sales-copilot-option-list">
          {card.options.map((option) => (
            <button
              key={option.id}
              type="button"
              className="sales-copilot-option-button"
              onClick={() => onSelectDisambiguation(card.groupId, option.id, option.label)}
              disabled={busyActionId === card.groupId}
            >
              <span>{option.label}</span>
              <span className="sales-copilot-option-meta">{formatValue(option.value)}</span>
            </button>
          ))}
        </div>
      </section>
    );
  }

  if (card.type === 'write_confirmation') {
    const isBusy = busyActionId === card.actionId;
    return (
      <section className="sales-copilot-card sales-copilot-card-highlight">
        <div className="sales-copilot-card-title">{card.title}</div>
        <div className="sales-copilot-card-subtitle">{card.summary}</div>
        <div className="sales-copilot-card-list-item compact">
          {Object.entries(card.fields).map(([key, value]) => (
            <div key={key} className="sales-copilot-kv-row">
              <span className="sales-copilot-kv-key">{toLabel(key)}</span>
              <span className="sales-copilot-kv-value">{formatValue(value)}</span>
            </div>
          ))}
        </div>
        <div className="sales-copilot-inline-actions">
          <button type="button" className="sales-copilot-primary-btn" onClick={() => onConfirmAction(card.actionId)} disabled={isBusy}>
            {isBusy ? 'Confirming...' : 'Confirm write'}
          </button>
          <button type="button" className="sales-copilot-secondary-btn" onClick={() => onCancelAction(card.actionId)} disabled={isBusy}>
            Cancel
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="sales-copilot-card sales-copilot-card-error">
      <div className="sales-copilot-card-title">{card.title}</div>
      <div className="sales-copilot-card-subtitle">{card.message}</div>
    </section>
  );
};

const ThreadView = ({
  activePendingAction,
  actionError,
  busyActionId,
  isResetting,
  onCancelAction,
  onConfirmAction,
  onNewChat,
  onSelectDisambiguation,
}: {
  activePendingAction: PendingCrmAction | null;
  actionError: string | null;
  busyActionId: string | null;
  isResetting: boolean;
  onCancelAction: (actionId?: string) => void;
  onConfirmAction: (actionId: string) => void;
  onNewChat: () => void;
  onSelectDisambiguation: (groupId: string, selectionId: string, label: string) => void;
}) => {
  const messages = useThread((state) => state.messages);
  const isRunning = useThread((state) => state.isRunning);

  return (
    <div className="sales-copilot-shell">
      <header className="sales-copilot-header">
        <div>
          <div className="sales-copilot-eyebrow">Mobile-first CRM assistant</div>
          <h1>Sales Copilot</h1>
          <p>
            Ask about customers, queries, followups, and travel guidance. CRM writes stay draft-only until you confirm them.
          </p>
        </div>
        <button type="button" className="sales-copilot-secondary-btn" onClick={onNewChat} disabled={isResetting || isRunning}>
          {isResetting ? 'Resetting...' : 'New chat'}
        </button>
      </header>

      {actionError && <div className="sales-copilot-alert">{actionError}</div>}

      {activePendingAction && (
        <section className="sales-copilot-pending-strip">
          <div>
            <div className="sales-copilot-strip-label">One confirmation pending</div>
            <strong>{activePendingAction.summary}</strong>
          </div>
          <div className="sales-copilot-inline-actions">
            <button
              type="button"
              className="sales-copilot-primary-btn"
              onClick={() => onConfirmAction(activePendingAction.id)}
              disabled={busyActionId === activePendingAction.id}
            >
              {busyActionId === activePendingAction.id ? 'Confirming...' : 'Confirm'}
            </button>
            <button
              type="button"
              className="sales-copilot-secondary-btn"
              onClick={() => onCancelAction(activePendingAction.id)}
              disabled={busyActionId === activePendingAction.id}
            >
              Cancel
            </button>
          </div>
        </section>
      )}

      <section className="sales-copilot-thread">
        {messages.length === 0 ? (
          <div className="sales-copilot-empty-state">
            <div className="sales-copilot-empty-card">
              <h2>Start a CRM conversation</h2>
              <p>Try “show my overdue followups”, “summarize customer Rahul Sharma”, or “create a followup for query 501 tomorrow”.</p>
            </div>
          </div>
        ) : (
          <ThreadPrimitive.Messages>
            {({ message }) => {
              const meta = message.role === 'assistant' ? getMessageMeta(message) : undefined;
              const text = getMessageText(message);

              return (
                <article key={message.id} className={`sales-copilot-message ${message.role === 'user' ? 'user' : 'assistant'}`}>
                  <div className="sales-copilot-message-header">
                    <span className="sales-copilot-role">{message.role === 'user' ? 'You' : 'Sales Copilot'}</span>
                    {meta?.provenance && <ProvenanceBadge provenance={meta.provenance} />}
                  </div>

                  {meta?.recentContext?.reused && (
                    <div className="sales-copilot-context-pill">
                      Using recent context{meta.recentContext.summary ? `: ${meta.recentContext.summary}` : ''}
                    </div>
                  )}

                  {text && <div className="sales-copilot-message-text">{text}</div>}

                  {meta?.errors?.length ? (
                    <div className="sales-copilot-error-list">
                      {meta.errors.map((error, index) => (
                        <div key={`${meta.requestId}-error-${index}`} className="sales-copilot-error-item">
                          {error}
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {meta?.cards?.length ? (
                    <div className="sales-copilot-card-stack">
                      {meta.cards.map((card, index) => (
                        <CardRenderer
                          key={`${meta.requestId}-${card.type}-${index}`}
                          card={card}
                          onConfirmAction={onConfirmAction}
                          onCancelAction={onCancelAction}
                          onSelectDisambiguation={onSelectDisambiguation}
                          busyActionId={busyActionId}
                        />
                      ))}
                    </div>
                  ) : null}
                </article>
              );
            }}
          </ThreadPrimitive.Messages>
        )}
      </section>

      <div className="sales-copilot-composer-wrap">
        <ComposerPrimitive.Root className="sales-copilot-composer">
          <ComposerPrimitive.Input
            className="sales-copilot-composer-input"
            placeholder="Ask about CRM records, followups, or travel advice"
            rows={1}
            maxRows={6}
            disabled={isResetting}
          />
          <div className="sales-copilot-composer-footer">
            <span className="sales-copilot-composer-hint">
              {activePendingAction ? 'Resolve the pending write or keep chatting for more context.' : 'Enter sends. Shift+Enter adds a new line.'}
            </span>
            <ComposerPrimitive.Send className="sales-copilot-primary-btn" disabled={isResetting}>
              {isRunning ? 'Working...' : 'Send'}
            </ComposerPrimitive.Send>
          </div>
        </ComposerPrimitive.Root>
      </div>
    </div>
  );
};

const SalesCopilotPage: React.FC = () => {
  const sessionIdRef = React.useRef<string | undefined>(undefined);
  const [activePendingAction, setActivePendingAction] = React.useState<PendingCrmAction | null>(null);
  const [busyActionId, setBusyActionId] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [isResetting, setIsResetting] = React.useState(false);

  const applyResponse = React.useCallback((response: CrmCopilotResponse) => {
    sessionIdRef.current = response.sessionId;
    setActivePendingAction(response.pendingAction);
    setActionError(response.errors[0] || null);
  }, []);

  const sendRequestRef = React.useRef<(message: string) => Promise<CrmCopilotResponse>>(
    async () => Promise.reject(new Error('Sales Copilot is not ready yet.'))
  );

  const adapterRef = React.useRef<ChatModelAdapter | undefined>(undefined);
  if (!adapterRef.current) {
    adapterRef.current = {
      run: async ({ messages }) => {
        const message = getLatestUserText(messages);
        const response = await sendRequestRef.current(message);
        return toAssistantResult(response);
      },
    };
  }

  const runtimeOptions = React.useMemo(() => ({ initialMessages: [] }), []);
  const runtime = useLocalRuntime(adapterRef.current, runtimeOptions);

  const sendRequest = React.useCallback(
    async (message: string) => {
      const response = await sendCrmCopilotRequest({
        message,
        sessionId: sessionIdRef.current,
      });
      applyResponse(response);
      return response;
    },
    [applyResponse]
  );

  React.useEffect(() => {
    sendRequestRef.current = sendRequest;
  }, [sendRequest]);

  const appendAssistantResponse = React.useCallback(
    (response: CrmCopilotResponse) => {
      const assistantResult = toAssistantResult(response);
      runtime.thread.append({
        role: 'assistant',
        content: assistantResult.content,
        metadata: assistantResult.metadata,
      });
    },
    [runtime]
  );

  const runClientAction = React.useCallback(
    async (clientAction: CrmClientAction, userLabel?: string) => {
      const nextBusyActionId =
        clientAction.type === 'confirm_action'
          ? clientAction.actionId
          : clientAction.type === 'select_disambiguation'
            ? clientAction.groupId
            : clientAction.type === 'cancel_action'
              ? clientAction.actionId || 'cancel_action'
              : 'new_chat';

      setBusyActionId(nextBusyActionId);
      setActionError(null);

      if (userLabel) {
        runtime.thread.append(userLabel);
      }

      try {
        const response = await sendCrmCopilotRequest({
          sessionId: sessionIdRef.current,
          clientAction,
        });
        applyResponse(response);
        appendAssistantResponse(response);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Sales Copilot request failed.';
        setActionError(message);
        runtime.thread.append({
          role: 'assistant',
          content: [{ type: 'text', text: message }],
          metadata: {
            custom: {
              [META_KEY]: {
                requestId: `local-${Date.now()}`,
                provenance: 'CRM data',
                cards: [],
                pendingAction: activePendingAction,
                disambiguation: null,
                recentContext: { reused: false, summary: null },
                errors: [message],
              } satisfies SalesCopilotMessageMeta,
            },
          },
        });
      } finally {
        setBusyActionId(null);
      }
    },
    [activePendingAction, appendAssistantResponse, applyResponse, runtime]
  );

  const handleNewChat = React.useCallback(async () => {
    if (!sessionIdRef.current) {
      runtime.thread.reset();
      setActivePendingAction(null);
      setActionError(null);
      return;
    }

    setIsResetting(true);
    setActionError(null);

    try {
      await sendCrmCopilotRequest({
        sessionId: sessionIdRef.current,
        clientAction: { type: 'new_chat' },
      });
      sessionIdRef.current = undefined;
      setActivePendingAction(null);
      runtime.thread.reset();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Unable to start a new chat right now.');
    } finally {
      setIsResetting(false);
    }
  }, [runtime]);

  return (
    <Layout>
      <AssistantRuntimeProvider runtime={runtime}>
        <ThreadView
          activePendingAction={activePendingAction}
          actionError={actionError}
          busyActionId={busyActionId}
          isResetting={isResetting}
          onCancelAction={(actionId) =>
            runClientAction({ type: 'cancel_action', actionId }, actionId ? 'Cancel pending action' : 'Cancel action')
          }
          onConfirmAction={(actionId) => runClientAction({ type: 'confirm_action', actionId }, 'Confirm pending action')}
          onNewChat={handleNewChat}
          onSelectDisambiguation={(groupId, selectionId, label) =>
            runClientAction({ type: 'select_disambiguation', groupId, selectionId }, `Selected: ${label}`)
          }
        />
      </AssistantRuntimeProvider>
    </Layout>
  );
};

export default SalesCopilotPage;
